import React, {useState, useEffect, useContext, useRef} from 'react'
import {I18nContext} from '@/context/i18n/I18nContext'
import {Tooltip} from 'react-tooltip'
import './AddRecipientsContent.scss'
import type {Recipient, WhoToAddKey, QuickSelectI18nScope} from '../QuickSelect'

interface Props {
  loading: boolean
  error: boolean
  currentUser: {id: number; role: string}
  recipientType: WhoToAddKey
  recipients: Recipient[]
  onRecipientClick: (recipient: Recipient) => void
  selectedRecipients: Recipient[]
  finalElement: React.ReactElement | null
  setSearchQuery: (_: string) => void
}

const hasRecipient = (selectedRecipients: Recipient[], recipient: Recipient) =>
  !!selectedRecipients.find(
    (selectedRecipient) =>
      recipient.type === selectedRecipient.type && recipient.id === selectedRecipient.id
  )

const normalizeSearchTerm = (text: string) => text.normalize('NFD').replace(/\p{Diacritic}/gu, '')

const matchesSearchTerm =
  (hasSearchTerm: boolean, searchTerm: string) => (recipient: Recipient) => {
    if (hasSearchTerm) {
      const regex = new RegExp(searchTerm, 'i')
      let matches = regex.test(normalizeSearchTerm(recipient.full_name))
      if (recipient.type === 'Student') {
        matches ||= regex.test(normalizeSearchTerm(recipient.searchable_class_list || ''))
        matches ||= !!recipient.parents?.some((parent) =>
          regex.test(normalizeSearchTerm(parent.full_name))
        )
      }
      return matches
    } else {
      return true
    }
  }

const EmptyState = ({recipientType}: {recipientType: WhoToAddKey}) => {
  const i18n = useContext(I18nContext) as QuickSelectI18nScope
  const i18nQS = i18n.quick_select
  const title = {
    user: i18nQS.recipient_list.empty.people,
    section: i18nQS.recipient_list.empty.classes,
    group: i18nQS.recipient_list.empty.groups,
  }

  return (
    <div className="empty-state">
      <div>
        <p className="empty-state-title">{title[recipientType]}</p>
        <p>{i18nQS.recipient_list.empty.suggestion}</p>
      </div>
    </div>
  )
}

const LoadingState = () => {
  return (
    <div className="loading-state" data-testid="loading-spinner">
      <i className="fa-solid fa-spinner-third fa-spin" />
    </div>
  )
}

const ErrorState = () => {
  const i18n = useContext(I18nContext) as QuickSelectI18nScope
  const i18nQS = i18n.quick_select

  return (
    <div className="empty-state">
      <div>
        <p className="empty-state-title">{i18nQS.recipient_list.error.header}</p>
        <p>{i18nQS.recipient_list.error.subheader}</p>
      </div>
    </div>
  )
}

const sortByLastAndFirstName = (a: Recipient, b: Recipient) => {
  if ((a.last_name || '') < (b.last_name || '')) return -1
  if ((a.last_name || '') > (b.last_name || '')) return 1
  // If first names are the same, sort by last name
  if ((a.first_name || '') < (b.first_name || '')) return -1
  if ((a.first_name || '') > (b.first_name || '')) return 1
  return 0 // If both first and last names are the same
}

const AddRecipientsContent = ({
  loading,
  error,
  recipients,
  onRecipientClick,
  selectedRecipients,
  recipientType,
  currentUser,
  finalElement,
  setSearchQuery,
}: Props) => {
  const i18n = useContext(I18nContext) as QuickSelectI18nScope
  const i18nQS = i18n.quick_select
  const [searchTerm, setSearchTerm] = useState('')
  const hasSearchTerm = recipientType === 'user' && searchTerm.trim() !== ''
  const recipientsToShow = recipients.filter(matchesSearchTerm(hasSearchTerm, searchTerm))
  if (recipientType === 'user') {
    recipientsToShow.sort(sortByLastAndFirstName)
  }

  const title: {[key in WhoToAddKey]: string} = {
    user: i18nQS.recipient_list.select_people,
    section: i18nQS.recipient_list.select_classes,
    group: i18nQS.recipient_list.select_groups,
  }

  const tooltipContentByRole: {[_: string]: string} = {
    teacher: i18nQS.recipient_list.tooltip.teacher,
    principal: i18nQS.recipient_list.tooltip.principal,
    staff: i18nQS.recipient_list.tooltip.staff,
    parent: i18nQS.recipient_list.tooltip.parent,
  }
  const [isInfoTooltipOpen, setIsInfoTooltipOpen] = useState(false)
  const tooltipContent =
    tooltipContentByRole[currentUser.role] || i18nQS.recipient_list.tooltip.generic

  const handleInfoTooltipKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Escape' && isInfoTooltipOpen) {
      // Close the tooltip first so Esc does not bubble and close the whole modal.
      e.preventDefault()
      e.stopPropagation()
      setIsInfoTooltipOpen(false)
    }
  }

  const liveRegionRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const announce = (message: string, delay: number) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (liveRegionRef.current) liveRegionRef.current.textContent = ''
    debounceRef.current = setTimeout(() => {
      if (liveRegionRef.current) liveRegionRef.current.textContent = message
    }, delay)
  }

  useEffect(() => {
    if (loading) {
      announce('Loading', 50)
    } else if (error) {
      announce(i18nQS.recipient_list.error.header, 50)
    } else if (recipientsToShow.length === 0) {
      announce(title[recipientType], 300)
    } else {
      announce(
        recipientsToShow.length === 1
          ? '1 result found'
          : `${recipientsToShow.length} results found`,
        300
      )
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, recipientsToShow.length, recipientType])

  useEffect(() => {
    if (recipientType === 'user') return
    const handler = setTimeout(() => setSearchQuery(searchTerm), 500)

    return () => {
      clearTimeout(handler)
    }
    // This needs to be fixed and the eslint rule re-enabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, recipientType])

  useEffect(() => {
    if (!isInfoTooltipOpen) return

    const handleDocumentEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return

      // Keep Escape behavior consistent even when tooltip is rendered in a body portal.
      setIsInfoTooltipOpen(false)

      // While this tooltip is open, Escape should close the tooltip first
      // (and not bubble to close the whole modal).
      e.preventDefault()
      e.stopPropagation()
    }

    document.addEventListener('keydown', handleDocumentEscape, true)
    return () => {
      document.removeEventListener('keydown', handleDocumentEscape, true)
    }
  }, [isInfoTooltipOpen])

  return (
    <div className="select-recipient">
      <div ref={liveRegionRef} className="sr-only" aria-live="polite" role="status" />
      <div className="select-recipient-title">
        <div className="select-recipient-title-text">{title[recipientType]}</div>
        {recipientType === 'user' && (
          <>
            <button
              type="button"
              data-tooltip-id="contextual-tooltip"
              data-tooltip-class-name="select-recipient-title-tooltip"
              className="fa-regular fa-circle-info select-recipient-info"
              aria-label={tooltipContent}
              aria-controls="contextual-tooltip"
              aria-expanded={isInfoTooltipOpen ? 'true' : 'false'}
              onClick={() => setIsInfoTooltipOpen((prevOpen) => !prevOpen)}
              onKeyDown={handleInfoTooltipKeyDown}
            />
            <Tooltip
              id="contextual-tooltip"
              place="right"
              content={tooltipContent}
              isOpen={isInfoTooltipOpen}
              setIsOpen={setIsInfoTooltipOpen}
              clickable
              delayHide={150}
              globalCloseEvents={{clickOutsideAnchor: true}}
            />
          </>
        )}
      </div>
      <div className="select-recipient-container">
        <div className="select-recipient-list">
          <div className="search-container">
            <i className="fa-regular fa-magnifying-glass search-icon" aria-hidden="true" />
            <input
              className="search-input"
              data-testid="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={i18nQS.recipient_list.search}
              aria-label={i18nQS.recipient_list.search}
            />
          </div>
          <div className="list-container">
            {error && <ErrorState />}
            {loading && <LoadingState />}
            {!loading && !error && recipientsToShow.length === 0 && (
              <EmptyState {...{recipientType}} />
            )}
            {!loading && !error && recipientsToShow.length > 0 && (
              <div data-testid="recipient-options" role="group" aria-label={title[recipientType]}>
                {recipientsToShow.map((recipient, index) => (
                  <label
                    data-testid="recipient-option-label"
                    key={index}
                    className={`list-item${index % 2 ? ' even' : ''} ${
                      hasRecipient(selectedRecipients, recipient) ? ' selected' : ''
                    }`}
                  >
                    <div className="left-selector-container">
                      <input
                        onChange={() => onRecipientClick(recipient)}
                        className="people-list-check"
                        type="checkbox"
                        checked={hasRecipient(selectedRecipients, recipient)}
                      />
                    </div>
                    <div className="right-content-container">
                      <div className="name">
                        {recipientType === 'user'
                          ? `${recipient.last_name}, ${recipient.first_name}`
                          : recipient.full_name}
                      </div>
                      <div className="description">{recipient.title}</div>
                    </div>
                  </label>
                ))}
                {finalElement}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddRecipientsContent
